import { Outlet } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAppDispatch } from '../store/hooks';
import { logout, useLogoutMutation } from '../../features/auth';
import { PrelineLoader } from './PrelineLoader';
import { Sidebar } from './Sidebar';
import { ErrorBoundary } from '../components/ErrorBoundary';


/**
 * Main Layout for authenticated routes
 *
 * Provides:
 * - Theme and global styles
 * - Sidebar with navigation and user menu
 * - Error notifications
 * - Outlet for child routes
 */
export function MainLayout() {
  const dispatch = useAppDispatch();
  const [logoutMutation] = useLogoutMutation();

  const [error, setError] = useState(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [isSidebarMinified, setIsSidebarMinified] = useState(false);

  useEffect(() => {
    // Check sidebar minified state on mount and when it changes
    const checkSidebarState = () => {
      const sidebar = document.getElementById('hs-application-sidebar');
      if (sidebar) {
        const isMinified = sidebar.classList.contains('minified');
        setIsSidebarMinified(isMinified);
      }
    };

    // Check initial state after a short delay to ensure Preline is loaded
    const timer = setTimeout(checkSidebarState, 100);

    // Create a MutationObserver to watch for class changes on the sidebar
    const sidebar = document.getElementById('hs-application-sidebar');
    if (sidebar) {
      const observer = new MutationObserver(checkSidebarState);
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class']
      });

      return () => {
        clearTimeout(timer);
        observer.disconnect();
      };
    }

    return () => clearTimeout(timer);
  }, []);

  const handleLogout = async () => {
    try {
      // Call backend logout to close Django session (for SSO users)
      await logoutMutation().unwrap();
    } catch (error) {
      // Ignore errors - logout locally even if backend fails
      console.warn('Backend logout failed, proceeding with local logout:', error);
    } finally {
      // Always clear local state and redirect
      dispatch(logout());
      window.location.href = '/login';
    }
  };

  const handleCloseError = () => {
    setErrorOpen(false);
    setError(null);
  };

  return (
    <>
      <PrelineLoader />
      <Sidebar onLogout={handleLogout} />

      {/* Main Content Area */}
      <div className={`w-full transition-all duration-300 pt-14 lg:pt-0 ${isSidebarMinified ? 'lg:ps-[60px]' : 'lg:ps-72'}`}>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </div>
    </>
  );
}
